// RoomPanel component for displaying rooms and their gateways

import React from 'react';
import Icon from '../../../core/Icon';
import { TEST_BEACONS, ROOM_TYPE_ICONS, ROOM_TYPE_ORDER } from '../constants';
import { generateGatewayId } from '../utils';
import {
  Panel,
  PanelTitle,
  RoomTypeSection,
  RoomTypeHeader,
  RoomGrid,
  RoomCardContainer,
  RoomName,
  GatewayInfo,
  BeaconsInRoom,
  BeaconDot,
} from '../styles';
import type { Room, RoomsByType, ExistingTestGateways, BeaconLocations, TestBeacon } from '../types';

interface RoomPanelProps {
  roomsByType: RoomsByType;
  selectedBeacon: string | null;
  existingTestGateways: ExistingTestGateways;
  beaconLocations: BeaconLocations;
  onRoomClick: (room: Room) => void;
}

interface RoomTypeSectionComponentProps {
  roomType: string;
  rooms: Room[];
  selectedBeacon: string | null;
  existingTestGateways: ExistingTestGateways;
  beaconLocations: BeaconLocations;
  onRoomClick: (room: Room) => void;
}

const getBeaconsInRoom = (room: Room, beaconLocations: BeaconLocations): TestBeacon[] => {
  const gatewayId = generateGatewayId(room);
  return TEST_BEACONS.filter(b => beaconLocations[b.id] === gatewayId);
};

const RoomTypeSectionComponent: React.FC<RoomTypeSectionComponentProps> = ({
  roomType,
  rooms,
  selectedBeacon,
  existingTestGateways,
  beaconLocations,
  onRoomClick,
}) => {
  if (rooms.length === 0) return null;

  return (
    <RoomTypeSection>
      <RoomTypeHeader>
        <Icon className="material-icons" style={{ fontSize: '1.2rem' }}>
          {ROOM_TYPE_ICONS[roomType] || 'room'}
        </Icon>
        {roomType} ({rooms.length})
      </RoomTypeHeader>
      <RoomGrid>
        {rooms.map(room => {
          const gwId = generateGatewayId(room);
          const hasGateway = !!existingTestGateways[gwId];
          const beaconsInRoom = getBeaconsInRoom(room, beaconLocations);

          return (
            <RoomCardContainer
              key={room.id}
              hasGateway={hasGateway}
              clickable={!!selectedBeacon}
              onClick={() => selectedBeacon && onRoomClick(room)}
            >
              <RoomName>{room.name}</RoomName>
              <GatewayInfo>
                {hasGateway ? (
                  <span style={{ color: '#4CAF50' }}>✓ {gwId}</span>
                ) : (
                  <span style={{ opacity: 0.5 }}>{gwId}</span>
                )}
              </GatewayInfo>
              {beaconsInRoom.length > 0 && (
                <BeaconsInRoom>
                  {beaconsInRoom.map(b => (
                    <BeaconDot key={b.id} color={b.color} title={b.label} />
                  ))}
                </BeaconsInRoom>
              )}
            </RoomCardContainer>
          );
        })}
      </RoomGrid>
    </RoomTypeSection>
  );
};

export const RoomPanel: React.FC<RoomPanelProps> = ({
  roomsByType,
  selectedBeacon,
  existingTestGateways,
  beaconLocations,
  onRoomClick,
}) => {
  // Get room types not in the standard order
  const otherRoomTypes = Object.keys(roomsByType).filter(type => !ROOM_TYPE_ORDER.includes(type));

  return (
    <Panel>
      <PanelTitle>
        Rooms {selectedBeacon && <span style={{ color: '#2196F3' }}>- Click to move beacon</span>}
      </PanelTitle>

      {/* Standard room types in order */}
      {ROOM_TYPE_ORDER.map(roomType => (
        <RoomTypeSectionComponent
          key={roomType}
          roomType={roomType}
          rooms={roomsByType[roomType] || []}
          selectedBeacon={selectedBeacon}
          existingTestGateways={existingTestGateways}
          beaconLocations={beaconLocations}
          onRoomClick={onRoomClick}
        />
      ))}

      {/* Other room types */}
      {otherRoomTypes.map(roomType => (
        <RoomTypeSectionComponent
          key={roomType}
          roomType={roomType}
          rooms={roomsByType[roomType] || []}
          selectedBeacon={selectedBeacon}
          existingTestGateways={existingTestGateways}
          beaconLocations={beaconLocations}
          onRoomClick={onRoomClick}
        />
      ))}
    </Panel>
  );
};
